import { useEffect, useLayoutEffect, useState } from 'react';

import { useParams, useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector, useEmojiWasm } from '@pkg/hooks';

import { YatPageVersion } from '@app/client';
import config from '@app/config';
import { getYatPageDetails } from '@app/store/actions/yat-page';
import { setLiquidIDExpandedPreview } from '@app/store/slices';

export function useProfilePageInit() {
    const dispatch = useAppDispatch();
    const { emojiWasm } = useEmojiWasm();
    const { yatParam: yat } = useParams();
    const [pageVersion, setPageVersion] = useState<YatPageVersion | null>(window.YAT_PV);
    const [isValidEmojiId, setIsValidEmojiId] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [pageDetailsLoaded, setPageDetailsLoaded] = useState(false);

    useLayoutEffect(() => {
        setIsLoading(true);
    }, [yat]);

    useEffect(() => {
        if (yat && emojiWasm) {
            const valid = emojiWasm.is_valid_emoji_id(yat, config.maxLengthYat);
            setIsValidEmojiId(valid);
        }
    }, [emojiWasm, yat]);

    useEffect(() => {
        dispatch(getYatPageDetails(yat as string, true, true)).then((r) => {
            if (!r?.ok && r?.status?.code == 404) {
                setPageVersion(YatPageVersion.NOT_FOUND);
            }
            if (r?.ok) {
                setPageVersion(r.value.version);
            }
            setPageDetailsLoaded(true);
        });
    }, [dispatch, yat]);

    // Check if the liquid ID expanded preview is enabled
    const [searchParams] = useSearchParams();
    const liquidIDExpandedPreview = useAppSelector((state) => state.yatPage.liquidIDExpandedPreview);

    useEffect(() => {
        const expandedPreview = searchParams.getAll('lid')?.[0];
        if (expandedPreview) {
            dispatch(setLiquidIDExpandedPreview(true));
        } else {
            dispatch(setLiquidIDExpandedPreview(false));
        }
    }, [dispatch, liquidIDExpandedPreview, searchParams]);

    useEffect(() => {
        if (pageDetailsLoaded && isValidEmojiId !== null) {
            setIsLoading(false);
        }
    }, [pageDetailsLoaded, isValidEmojiId]);

    return { isValidEmojiId, isNotFound: pageVersion === YatPageVersion.NOT_FOUND, pageVersion, eid: yat, isLoading };
}

declare global {
    interface Window {
        YAT_PV: YatPageVersion | null;
    }
}
